import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { SettingsPanel, SettingsSection, SettingsSelect } from "../Settings";
import { SUPPORTED_LANGUAGES } from "../../../i18n";
import { ALLOWED_THEMES, DEFAULT_SETTINGS } from "../../../constants/profileSettings";

const AppearanceSettings = ({ settings, onChange }) => {
    const { t } = useTranslation();
    const resolvedSettings = {
        theme: settings?.theme || DEFAULT_SETTINGS.appearance.theme,
        fontSize: settings?.fontSize || DEFAULT_SETTINGS.appearance.fontSize,
        language: settings?.language || DEFAULT_SETTINGS.appearance.language,
    };

    const handleSelect = (key) => (value) => {
        onChange({ [key]: value });
    };

    const themeOptions = useMemo(() => (
        ALLOWED_THEMES.map((theme) => ({
            value: theme,
            label: t(`appearance.options.themes.${theme}`),
        }))
    ), [t]);

    const fontSizeOptions = useMemo(() => ([
        { value: "small", label: t("appearance.options.fontSizes.small") },
        { value: "medium", label: t("appearance.options.fontSizes.medium") },
        { value: "large", label: t("appearance.options.fontSizes.large") },
    ]), [t]);

    const languageOptions = useMemo(() => (
        SUPPORTED_LANGUAGES.map((code) => ({
            value: code,
            label: t(`appearance.options.languages.${code}`),
        }))
    ), [t]);

    return (
        <SettingsPanel
            title={t("appearance.panelTitle")}
            description={t("appearance.panelDescription")}
        >
            <SettingsSection
                title={t("appearance.sections.themeTitle")}
                description={t("appearance.sections.themeDescription")}
            >
                <SettingsSelect
                    name="appearance-theme"
                    label={t("appearance.sections.themeLabel")}
                    value={resolvedSettings.theme}
                    onChange={handleSelect("theme")}
                    options={themeOptions}
                />
            </SettingsSection>

            <SettingsSection
                title={t("appearance.sections.typographyTitle")}
                description={t("appearance.sections.typographyDescription")}
            >
                <SettingsSelect
                    name="appearance-font-size"
                    label={t("appearance.sections.fontLabel")}
                    value={resolvedSettings.fontSize}
                    onChange={handleSelect("fontSize")}
                    options={fontSizeOptions}
                />
            </SettingsSection>

            <SettingsSection
                title={t("appearance.sections.languageTitle")}
                description={t("appearance.sections.languageDescription")}
            >
                <SettingsSelect
                    name="appearance-language"
                    label={t("appearance.sections.languageLabel")}
                    value={resolvedSettings.language}
                    onChange={handleSelect("language")}
                    options={languageOptions}
                />
            </SettingsSection>
        </SettingsPanel>
    );
};

AppearanceSettings.propTypes = {
    settings: PropTypes.shape({
        theme: PropTypes.string,
        fontSize: PropTypes.string,
        language: PropTypes.string,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
};

export default AppearanceSettings;
